import React from 'react';
import { Helmet } from 'react-helmet-async';
import HeroSection from '../components/HeroSection';
import Reveal      from '../components/Reveal';

const EFFECTIVE_DATE = 'January 1, 2025';

const SECTIONS = [
  {
    id:    'information-collected',
    title: 'Information We Collect',
    content: (
      <>
        <p>We collect information you provide directly to us, information we collect automatically when you visit our websites, and information from third-party sources. The types of information we may collect include:</p>
        <ul>
          <li><strong>Contact Information:</strong> Name, email address, phone number, and mailing address provided through contact forms, inquiries, or account registration.</li>
          <li><strong>Communications:</strong> Records of correspondence you initiate with us, including email inquiries, contact form submissions, and business communications.</li>
          <li><strong>Technical Data:</strong> IP address, browser type and version, operating system, referring URLs, pages visited, time spent on pages, and other diagnostic data collected automatically when you use our websites.</li>
          <li><strong>Usage Data:</strong> Information about how you navigate and interact with our websites, including click patterns, scroll behavior, and feature engagement.</li>
          <li><strong>Cookies and Tracking Technologies:</strong> Data collected through cookies, web beacons, pixel tags, and similar technologies as described in the Cookies section below.</li>
        </ul>
        <p>We do not knowingly collect sensitive personal information such as financial account numbers, government identification numbers, health data, or biometric identifiers through our websites.</p>
      </>
    ),
  },
  {
    id:    'how-we-use',
    title: 'How We Use Your Information',
    content: (
      <>
        <p>We use the information we collect for the following purposes:</p>
        <ul>
          <li>To respond to your inquiries, communications, and requests submitted through our contact forms or by email.</li>
          <li>To operate, maintain, and improve our websites and the services provided through them.</li>
          <li>To analyze website performance, user behavior, and content engagement to enhance user experience.</li>
          <li>To send administrative communications, such as responses to inquiries or notifications related to your use of our services.</li>
          <li>To comply with applicable legal obligations, enforce our terms and policies, and protect the rights and safety of Driven 2 LLC, its portfolio brands, and others.</li>
          <li>To detect, prevent, and address technical issues, fraudulent activity, and security incidents.</li>
        </ul>
        <p>We do not sell your personal information to third parties. We do not use personal information collected through our websites for automated decision-making that produces significant legal effects.</p>
      </>
    ),
  },
  {
    id:    'sharing',
    title: 'Sharing and Disclosure',
    content: (
      <>
        <p>We may share your personal information in the following limited circumstances:</p>
        <ul>
          <li><strong>Service Providers:</strong> We may share information with third-party vendors and service providers that perform services on our behalf, such as website hosting, analytics, email delivery, and customer relationship management. These providers are contractually required to use your information only to provide services to us and to maintain appropriate security standards.</li>
          <li><strong>Portfolio Brands:</strong> We may share information across the Driven 2 LLC portfolio of brands (DriveTalkAI, AddyMay, Driven2Win, PIC and SAV) for internal operational and administrative purposes consistent with this policy.</li>
          <li><strong>Legal Requirements:</strong> We may disclose information if required to do so by law, regulation, legal process, or governmental request, or when we believe in good faith that disclosure is necessary to protect the rights, property, or safety of Driven 2 LLC or others.</li>
          <li><strong>Business Transactions:</strong> In the event of a merger, acquisition, reorganization, sale of assets, or other corporate transaction, personal information may be transferred as part of that transaction. We will provide notice before personal information is transferred and becomes subject to a different privacy policy.</li>
        </ul>
        <p>We do not share personal information with third parties for their own marketing purposes without your explicit consent.</p>
      </>
    ),
  },
  {
    id:    'data-retention',
    title: 'Data Retention',
    content: (
      <>
        <p>We retain personal information for as long as necessary to fulfill the purposes for which it was collected, including to satisfy any legal, accounting, or reporting obligations. The criteria we use to determine retention periods include:</p>
        <ul>
          <li>The nature of the information and the purpose for which it was collected.</li>
          <li>Whether there is an ongoing business need to retain the information (such as an active inquiry or business relationship).</li>
          <li>Whether we are subject to any legal obligation to retain the information.</li>
          <li>Whether retention is advisable in light of our legal position, such as applicable statutes of limitations.</li>
        </ul>
        <p>When personal information is no longer needed, we take reasonable steps to delete or de-identify it in a secure manner.</p>
      </>
    ),
  },
  {
    id:    'security',
    title: 'Data Security',
    content: (
      <>
        <p>We implement and maintain reasonable technical, administrative, and physical safeguards designed to protect your personal information from unauthorized access, use, disclosure, alteration, or destruction. Our security practices include:</p>
        <ul>
          <li>Encrypted transmission of data in transit using SSL/TLS protocols.</li>
          <li>Access controls limiting personal information access to authorized personnel with a legitimate need.</li>
          <li>Regular review of our information collection, storage, and processing practices.</li>
          <li>Vendor security assessment for third-party service providers with access to personal information.</li>
        </ul>
        <p>No method of data transmission over the internet or electronic storage is 100% secure. While we strive to use commercially acceptable means to protect your personal information, we cannot guarantee its absolute security. In the event of a data breach that affects your rights and freedoms, we will notify affected individuals as required by applicable law.</p>
      </>
    ),
  },
  {
    id:    'cookies',
    title: 'Cookies and Tracking Technologies',
    content: (
      <>
        <p>Our websites use cookies and similar tracking technologies to enhance functionality and analyze site performance. Cookies are small text files placed on your device when you visit a website.</p>
        <p>We use the following categories of cookies:</p>
        <ul>
          <li><strong>Essential Cookies:</strong> Necessary for the basic operation of our websites. These cannot be disabled without affecting website functionality.</li>
          <li><strong>Analytics Cookies:</strong> Used to collect aggregate information about how visitors use our websites, including pages visited, traffic sources, and time on site. This data is used to improve website performance and user experience.</li>
          <li><strong>Preference Cookies:</strong> Used to remember your preferences and settings to improve your experience on return visits.</li>
        </ul>
        <p>Most web browsers allow you to control cookies through browser settings. You may set your browser to refuse cookies or to alert you when cookies are being sent. Note that disabling certain cookies may affect the functionality of our websites. For more information about managing cookies, refer to your browser's help documentation.</p>
      </>
    ),
  },
  {
    id:    'third-party-links',
    title: 'Third-Party Links',
    content: (
      <>
        <p>Our websites may contain links to third-party websites, services, or applications — including the individual websites of our portfolio brands (DriveTalkAI.com, AddyMay.com, Driven2win.com, PICandSAV.com) and external references. This Privacy Policy applies only to the Driven 2 LLC holding company website (driven2.net).</p>
        <p>We do not control, and are not responsible for, the content, privacy practices, or terms of third-party websites. We encourage you to review the privacy policy of any website you visit. The inclusion of a link does not imply our endorsement of the linked site.</p>
      </>
    ),
  },
  {
    id:    'childrens-privacy',
    title: "Children's Privacy",
    content: (
      <>
        <p>Our websites are not directed to children under the age of 13, and we do not knowingly collect personal information from children under 13. If you are a parent or guardian and believe we have inadvertently collected information from a child under 13, please contact us immediately at info@driven2.net so that we can take appropriate steps to delete the information.</p>
        <p>If we learn that we have collected personal information from a child under 13 without verification of parental consent, we will take steps to remove that information from our systems promptly.</p>
      </>
    ),
  },
  {
    id:    'user-rights',
    title: 'Your Rights and Choices',
    content: (
      <>
        <p>Depending on your location and applicable law, you may have certain rights with respect to your personal information, including:</p>
        <ul>
          <li><strong>Access:</strong> The right to request confirmation of whether we process personal information about you and to obtain a copy of that information.</li>
          <li><strong>Correction:</strong> The right to request that we correct inaccurate or incomplete personal information about you.</li>
          <li><strong>Deletion:</strong> The right to request that we delete your personal information, subject to certain exceptions.</li>
          <li><strong>Objection / Restriction:</strong> The right to object to or request restriction of certain processing of your personal information.</li>
          <li><strong>Data Portability:</strong> The right to receive your personal information in a structured, commonly used format where technically feasible.</li>
        </ul>
        <p>To exercise any of these rights, please contact us at info@driven2.net. We will respond to your request within the time frame required by applicable law. We may need to verify your identity before processing certain requests.</p>
      </>
    ),
  },
  {
    id:    'california',
    title: 'California Privacy Rights (CCPA/CPRA)',
    content: (
      <>
        <p>If you are a California resident, you have specific rights under the California Consumer Privacy Act (CCPA) as amended by the California Privacy Rights Act (CPRA), including:</p>
        <ul>
          <li>The right to know what personal information we collect, use, disclose, and sell about you.</li>
          <li>The right to request deletion of personal information we have collected from you, subject to certain exceptions.</li>
          <li>The right to opt out of the sale or sharing of your personal information. We do not sell personal information as that term is defined under the CCPA.</li>
          <li>The right to non-discrimination for exercising your CCPA rights.</li>
          <li>The right to correct inaccurate personal information.</li>
          <li>The right to limit the use and disclosure of sensitive personal information.</li>
        </ul>
        <p>To submit a California privacy rights request, contact us at info@driven2.net with the subject line "California Privacy Request." We will respond within 45 days of receiving your verifiable consumer request, or notify you if we require additional time.</p>
      </>
    ),
  },
  {
    id:    'international',
    title: 'International Users',
    content: (
      <>
        <p>Driven 2 LLC is based in Florida, United States. Our websites are operated from the United States. If you are accessing our websites from outside the United States, please be aware that your information may be transferred to, stored, and processed in the United States, where data protection laws may differ from those in your jurisdiction.</p>
        <p>If you are located in the European Economic Area (EEA), the United Kingdom, or another jurisdiction with data protection requirements, we rely on applicable legal bases for processing your personal information, which may include your consent, the performance of a contract, compliance with a legal obligation, or our legitimate interests.</p>
        <p>By using our websites, you acknowledge and consent to the transfer and processing of your information in the United States as described in this Privacy Policy.</p>
      </>
    ),
  },
  {
    id:    'policy-changes',
    title: 'Changes to This Privacy Policy',
    content: (
      <>
        <p>We may update this Privacy Policy from time to time to reflect changes in our practices, technology, legal requirements, or other factors. When we make material changes, we will update the effective date at the top of this policy.</p>
        <p>We encourage you to review this Privacy Policy periodically to stay informed about how we are protecting your information. Your continued use of our websites following the posting of changes constitutes your acceptance of those changes.</p>
        <p>If we make changes that materially affect how we use or share personal information previously collected, we will take reasonable steps to notify affected individuals, which may include posting a notice on our website or sending an email to the address on file.</p>
      </>
    ),
  },
  {
    id:    'contact-us',
    title: 'Contact Us',
    content: (
      <>
        <p>If you have questions, concerns, or requests regarding this Privacy Policy or our data practices, please contact us using the following information:</p>
        <ul>
          <li><strong>Company:</strong> Driven 2 LLC</li>
          <li><strong>Email:</strong> <a href="mailto:info@driven2.net" style={{ color: 'var(--navy-700)' }}>info@driven2.net</a></li>
          <li><strong>Phone:</strong> <a href="tel:9046387199" style={{ color: 'var(--navy-700)' }}>904-638-7199</a></li>
        </ul>
        <p>We take all privacy inquiries seriously and will make reasonable efforts to respond promptly and address your concerns in good faith.</p>
      </>
    ),
  },
];

export default function PrivacyPolicy() {
  return (
    <>
      <Helmet>
        <title>Privacy Policy — Driven 2 LLC</title>
        <meta
          name="description"
          content="Driven 2 LLC Privacy Policy — how we collect, use, share, and protect personal information across our holding company website and digital brand portfolio."
        />
        <meta property="og:title" content="Privacy Policy — Driven 2 LLC" />
        <meta property="og:description" content="Read the Driven 2 LLC Privacy Policy covering data collection, use, sharing, your rights, and how we protect your information." />
        <meta property="og:url" content="https://www.driven2.net/privacy-policy" />
        <link rel="canonical" href="https://www.driven2.net/privacy-policy" />
        <meta name="robots" content="noindex, follow" />
      </Helmet>

      {/*
        ═══════════════════════════════════════════
        HERO — MIX: MINIMAL / LIGHT OVERLAY
        PLACEHOLDER DIRECTION:
        "Minimal, sophisticated privacy and data
        stewardship visual — abstract architectural
        geometry, subtle lock or grid motif,
        enterprise-grade compliance atmosphere.
        Very clean and restrained. Medium overlay,
        dark text could work if image is light-toned,
        OR light text on a dark abstract image."

        TO REPLACE: add bgImage="/images/hero-privacy.jpg"
        ═══════════════════════════════════════════
      */}
      <HeroSection
        eyebrow="Legal"
        title="Privacy Policy"
        subtitle={`Effective Date: ${EFFECTIVE_DATE}`}
        size="small"
        align="center"
        bgImage="https://images.unsplash.com/photo-1614064641938-3bbee52942c7?auto=format&fit=crop&w=1920&q=80"
      />

      {/* ─── POLICY BODY ─── */}
      <section className="section">
        <div className="container">
          <div className="policy-wrap">

            {/* Intro */}
            <Reveal>
              <div style={{ padding: 'var(--sp-10)', background: 'var(--off-white)', border: '1px solid var(--gray-200)', borderLeft: '3px solid var(--gold-500)', marginBottom: 'var(--sp-12)' }}>
                <p style={{ fontSize: 'var(--text-base)', color: 'var(--gray-700)', lineHeight: 1.82, marginBottom: 'var(--sp-4)' }}>
                  Driven 2 LLC ("Driven 2," "we," "our," or "us") is a private holding company
                  based in Florida. This Privacy Policy describes how we
                  collect, use, disclose, and protect personal information obtained through our
                  website at <strong>www.driven2.net</strong> and through our communications with
                  you.
                </p>
                <p style={{ fontSize: 'var(--text-base)', color: 'var(--gray-700)', lineHeight: 1.82, marginBottom: 0 }}>
                  By accessing or using our website, you agree to the collection and use of your
                  information as described in this policy. If you do not agree with the terms of
                  this Privacy Policy, please discontinue use of our website.
                </p>
              </div>
            </Reveal>

            {/* Table of Contents */}
            <Reveal>
              <div style={{ marginBottom: 'var(--sp-12)', padding: 'var(--sp-8)', background: 'var(--gray-50)', border: '1px solid var(--gray-200)' }}>
                <div style={{ fontSize: 'var(--text-xs)', fontWeight: 600, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--gold-600)', marginBottom: 'var(--sp-5)' }}>
                  Contents
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--sp-2)' }}>
                  {SECTIONS.map(({ id, title }, i) => (
                    <a
                      key={id}
                      href={`#${id}`}
                      style={{ fontSize: 'var(--text-sm)', color: 'var(--navy-600)', textDecoration: 'none', display: 'flex', gap: 'var(--sp-2)', alignItems: 'flex-start', lineHeight: 1.5, padding: '2px 0', transition: 'color 150ms' }}
                      onMouseEnter={e => e.currentTarget.style.color = 'var(--gold-600)'}
                      onMouseLeave={e => e.currentTarget.style.color = 'var(--navy-600)'}
                    >
                      <span style={{ color: 'var(--gold-500)', flexShrink: 0, fontSize: 'var(--text-xs)' }}>{String(i + 1).padStart(2, '0')}</span>
                      {title}
                    </a>
                  ))}
                </div>
              </div>
            </Reveal>

            {/* Policy Sections */}
            {SECTIONS.map(({ id, title, content }, i) => (
              <Reveal key={id} delay={0.05}>
                <section id={id} className="policy-section">
                  <h2>{title}</h2>
                  {content}
                </section>
              </Reveal>
            ))}

            {/* Footer note */}
            <Reveal>
              <div style={{ marginTop: 'var(--sp-12)', paddingTop: 'var(--sp-8)', borderTop: '1px solid var(--gray-200)', textAlign: 'center' }}>
                <p style={{ fontSize: 'var(--text-sm)', color: 'var(--gray-500)' }}>
                  This Privacy Policy was last updated on <strong>{EFFECTIVE_DATE}</strong>.
                  For questions, email{' '}
                  <a href="mailto:info@driven2.net" style={{ color: 'var(--navy-700)' }}>
                    info@driven2.net
                  </a>.
                </p>
              </div>
            </Reveal>

          </div>
        </div>
      </section>
    </>
  );
}
